import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AppInit from "./components/common/AppInit";
import BottomNav from "./components/common/BottomNav";
import Notice from "./components/common/Notice";
import OfflineBanner from "./components/common/OfflineBanner";
import RequireHost from "./components/common/RequireHost";
import RequireSession from "./components/common/RequireSession";
import HomePage from "./components/home/HomePage";
import JoinByCodePage from "./components/home/JoinByCodePage";
import JoinPage from "./components/home/JoinPage";
import WelcomePage from "./components/home/WelcomePage";
import EndedPage from "./components/home/EndedPage";
import LivePage from "./components/live/LivePage";
import ProjectorPage from "./components/live/ProjectorPage";
import SongsPage from "./components/songs/SongsPage";
import SongPage from "./components/songs/SongPage";
import SongEditorPage from "./components/songs/SongEditorPage";
import SongImportPage from "./components/songs/SongImportPage";
import FavoritesPage from "./components/library/FavoritesPage";
import SetsPage from "./components/sets/SetsPage";
import SetEditorPage from "./components/sets/SetEditorPage";
import SettingsPage from "./components/settings/SettingsPage";
import IssuesPage from "./components/issues/IssuesPage";
import { selectHasSong, selectIsHost } from "./selectors";
import { showsLiveBar, showsTabs } from "./lib/navigation";
import "./App.css";

const withSession = (element) => <RequireSession>{element}</RequireSession>;
const withHost = (element) => withSession(<RequireHost>{element}</RequireHost>);
// a host with a key browses and hosts their sets even between sessions
const withSessionOrKey = (element) => <RequireSession orHostKey>{element}</RequireSession>;

function App() {
  const { pathname } = useLocation();
  const hasSession = useSelector((state) => Boolean(state.session.Code));
  const isHost = useSelector(selectIsHost);
  const hasSong = useSelector(selectHasSong);
  const tabs = showsTabs(pathname, { hasSession, isHost });
  const liveBar = tabs && showsLiveBar(pathname, { hasSession, isHost }) && (isHost || hasSong);
  const wide = pathname === "/screen";
  const guestLive = pathname === "/lyrics" && hasSession && !isHost; // fills the viewport and scrolls inside itself

  const className = ["app", wide && "app-wide", pathname === "/" && "app-start", guestLive && "app-live", tabs && "has-nav", liveBar && "has-livebar"].filter(Boolean).join(" ");

  return (
    <div id="app" className={className}>
      <div id="app-body" className="app-body">
        <AppInit />
        <OfflineBanner />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/welcome" element={withSession(<WelcomePage />)} />
          <Route path="/lyrics" element={withSession(<LivePage />)} />
          <Route path="/screen" element={withSession(<ProjectorPage />)} />
          <Route path="/songs" element={withSession(<SongsPage />)} />
          <Route path="/songs/new" element={withHost(<SongEditorPage />)} />
          <Route path="/songs/import" element={withHost(<SongImportPage />)} />
          <Route path="/songs/:id/edit" element={withHost(<SongEditorPage />)} />
          <Route path="/songs/:id" element={withSession(<SongPage />)} />
          <Route path="/library" element={withSession(<FavoritesPage />)} />
          <Route path="/sets" element={withSessionOrKey(<SetsPage />)} />
          <Route path="/sets/:id" element={withSessionOrKey(<SetEditorPage />)} />
          <Route path="/queue" element={withSession(<SetEditorPage live />)} />
          <Route path="/join" element={withSession(<JoinPage />)} />
          <Route path="/settings" element={withSession(<SettingsPage />)} />
          <Route path="/issues" element={withHost(<IssuesPage />)} />
          <Route path="/ended" element={<EndedPage />} />
          <Route path="/catalog" element={<Navigate to="/songs" replace />} />
          {/* share links look like https://sing.at/ABCDE */}
          <Route path="/:code" element={<JoinByCodePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Notice />
        {tabs && <BottomNav liveBar={liveBar} />}
      </div>
    </div>
  );
}

export default App;
